import {createSelector} from '@reduxjs/toolkit'
import {pathOr} from 'ramda'

import {
  getBrand,
  getBrandShowSignIn,
  getIsBrandOffersLockedByDefault,
  State as BrandState
} from '../../_web/brand/modules/selectors'
import {getQueryStringObject} from '../../_web/utils/url'
import {
  hasAudienceTokenInUrl,
  hasUnlockPlusTokenInUrl,
  PlusTiers,
  UserTier
} from '../../offer/business/privateDeals'
import {GenericOffer, Offer} from '../../offer/types/offer'
import {SearchOffer} from '../../offer/types/SearchOffer'
import {safeJsonParse} from '../../utils/object'
import {getEmailDomain} from '../business/authentication'
import {getCognitoUserTierFromCookie} from '../business/persistData'
import {getUserPoolName} from '../services'
import {
  CustomAppDataAttributes,
  User,
  UserAttributeIdentities,
  UserIdentifiableTraits
} from '../types/Cognito'
import {
  Connections,
  getCognitoCustomAppData,
  getCognitoUserTier,
  getConnection,
  getIsUserTierEmployee
} from './selectors.shared'
import {StateType, SusiHistoryState} from './slice'
import {updateUserAttributes} from './thunks'
import {isPrivateDeal} from './utils'

const AUTHENTICATION_PATH = 'authentication'

export type AuthenticationStateType = {
  authentication: StateType
}

/**
 * Selector that returns the profile picture
 * @param state - the redux state holding the authentication info
 */
export const getProfilePicture = (state: AuthenticationStateType) =>
  state[AUTHENTICATION_PATH].user?.attributes?.picture ||
  state[AUTHENTICATION_PATH].whiteLabelUser?.attributes?.picture

/**
 * Selector that returns the white label user
 * @param state - the redux state holding the authentication info
 */
export const getWhiteLabelUser = (state: AuthenticationStateType) =>
  state[AUTHENTICATION_PATH].whiteLabelUser

/**
 * Selector that returns the white label user id
 * @param state - the redux state holding the authentication info
 */
export const getWhiteLabelUserId = (state: AuthenticationStateType) =>
  state[AUTHENTICATION_PATH].whiteLabelUser?.attributes?.sub

/**
 * Returns the stored user id
 */
export const getUserId: (user?: StateType['user']) => string | null = pathOr(
  null,
  ['attributes', 'sub']
)

/**
 * Selector that returns the user's authentication error
 * @param state - the redux state holding the authentication info
 */
const getAuthenticationError = (state: AuthenticationStateType) =>
  state[AUTHENTICATION_PATH].error

/**
 * Selector that returns the authentication error
 */
export const selectAuthenticationError = createSelector(
  getAuthenticationError,
  error => error
)

/**
 * Returns whether the user is member plus or not
 * @param user - Cognito user
 */
export const getCognitoIsMemberPlus = (user?: StateType['user']) =>
  getCognitoHasPlusTier(getCognitoUserTier(user)) || false

/**
 * Checks whether the user has any plus tier (is a plus member or not)
 * @param userTier - The user tier values from Cognito
 * @returns boolean
 */
export const getCognitoHasPlusTier = (userTier: UserTier[] | []) =>
  userTier.some(tier => PlusTiers.includes(tier))

/**
 * Selector that returns whether the UI visibility state of the passwordless login overlay
 * @param state - the redux state holding the authentication info
 */
export const isPasswordlessLoginVisible = (state: AuthenticationStateType) =>
  state.authentication?.isPasswordlessLoginVisible

/**
 * Selector that returns passwordless overlay source component parameter
 * @param state - the redux state holding the authentication info
 */
export const getPasswordlessLoginSourceComponent = (
  state: AuthenticationStateType
) => state.authentication?.sourceComponent

/**
 * Get Cognito User Id
 * @param user - Cognito user object
 */
export const getCognitoUserId = (user?: User | null) => user?.attributes?.sub

/**
 * Get auth0_id from migrated users
 * @param user - Cognito user
 * @returns string
 */
export const getUserLegacyId = (user?: User) => {
  const customAppData = getCognitoCustomAppData(user)
  return customAppData?.auth0_id || null
}

/**
 * Get the current user tier.
 * Different tiers can be offered different rates and experiences
 * @param user - Cognito user
 * @returns ['employee', 'plus']
 */
export const getUserTier = (user?: User | null) =>
  getCognitoCustomAppData(user)?.tier || []

/**
 * Returns which {@link AuthenticationFlows | authentication flow} the user is trying to use
 * @param state - the redux state holding the authentication info
 */
export const getAuthenticationFlow = (state: AuthenticationStateType) =>
  state[AUTHENTICATION_PATH].flow

/**
 * Returns the hotelId where the user clicked to unlock the offer
 * @param state - the redux state holding the authentication info
 */
export const getUnlockHotelId = (state: AuthenticationStateType) =>
  state[AUTHENTICATION_PATH].unlockHotelId

/**
 * Selector that returns if the user should see offers unlocked
 * @param state - the redux state holding the authentication info
 */
export const getShouldSeeOffersUnlockedStateProp = (
  state: AuthenticationStateType
) => state[AUTHENTICATION_PATH].shouldSeeOffersUnlocked

/**
 * Selector that returns if the user is authenticated
 * @param state - the redux state holding the authentication info
 */
export const getIsAuthenticated = (state: AuthenticationStateType) =>
  state[AUTHENTICATION_PATH].isAuthenticated

/**
 * Computed selector that returns if the user should see offers unlocked.
 * This selector only depends on Redux state and does not access URL params.
 *
 * For URL params logic (unlock plus token, audience token), use the client package
 * selector that composes this with getUrlParams from the search module.
 *
 * This fixes the bug where the selector only read from Redux state,
 * missing brand-specific logic like offersLockedByDefault: false on some brands
 * @param state - The authentication state (and brand state via getIsBrandOffersLockedByDefault)
 * @returns true if offers should be unlocked based on Redux state, auth status, and brand settings
 */
// packages/core/src/auth/modules/selectors.ts
export const getShouldSeeOffersUnlocked = createSelector(
  [
    getShouldSeeOffersUnlockedStateProp,
    getIsAuthenticated,
    getIsBrandOffersLockedByDefault
  ],
  (
    shouldSeeOffersUnlocked,
    isAuthenticated,
    brandOffersLockedByDefault
  ): boolean => {
    // Check URL params for unlock tokens (audience token or plus token)
    const urlParams = getQueryStringObject()
    const hasAudienceToken = hasAudienceTokenInUrl(urlParams)
    const hasUnlockPlusToken = hasUnlockPlusTokenInUrl(urlParams)

    // Return true if any of these conditions are met:
    // 1. Redux state says offers should be unlocked (set when user authenticates)
    // 2. User is authenticated
    // 3. Brand has offersLockedByDefault: false
    // 4. URL has audience token (token=lkjs82)
    // 5. URL has unlock plus token (token=7fe276)
    return (
      shouldSeeOffersUnlocked ||
      isAuthenticated ||
      brandOffersLockedByDefault === false ||
      hasAudienceToken ||
      hasUnlockPlusToken
    )
  }
)

/**
 * Selector that returns if the user is authenticated
 */
export const selectIsAuthenticated = createSelector(
  getIsAuthenticated,
  isAuthenticated => isAuthenticated
)

/**
 * Selector that returns the user
 * @param state - the redux state holding the authentication info
 */
export const getUser = (state: AuthenticationStateType) =>
  state[AUTHENTICATION_PATH].user

/**
 * Selector that returns the loading state
 * @param state - the redux state holding the authentication info
 */
export const getIsLoading = (state: AuthenticationStateType) =>
  state[AUTHENTICATION_PATH].isLoading

/**
 * Selector that returns the loading state
 */
export const selectIsLoading = createSelector(
  getIsLoading,
  isLoading => isLoading
)

/**
 * Selector that returns the user id
 * @param user - Cognito user
 * @returns string
 */
export const selectUserId = createSelector(getUser, getCognitoUserId)

/**
 * Selector that returns the user email
 * @param user - Cognito user
 * @returns string
 */
export const getUserEmail = (user?: User | null) => user?.attributes?.email

/**
 * Selector that returns whether the user is a privacy restricted user
 * @param state - the redux state holding the authentication info
 * @returns boolean
 */
export const getIsPrivacyRestrictedUser = createSelector(
  getUser,
  user =>
    safeJsonParse<CustomAppDataAttributes>(
      user?.attributes?.['custom:app_data']
    )?.privacy_restricted ?? false
)

/**
 * Selector that returns the user email
 * @param user - Cognito user
 * @returns string
 */
export const selectUserEmail = createSelector(getUser, getUserEmail)

const vioEmailDomains = ['@vio.com', '@appunite.vio.com', '@eliftech.vio.com']
/**
 * Selector that checks if the user has a Vio email domain
 */
export const selectHasVioEmail = createSelector(selectUserEmail, userEmail =>
  vioEmailDomains.includes(getEmailDomain(userEmail) ?? '')
)

/**
 * Like `selectHasVioEmail`, but also accepts any `vio.com` subdomain and
 * `findhotel.net`.
 */
export const selectIsInternalUser = createSelector(
  selectUserEmail,
  userEmail => {
    const domain = getEmailDomain(userEmail)?.toLowerCase()
    return (
      domain === '@vio.com' ||
      domain === '@findhotel.net' ||
      Boolean(domain?.endsWith('.vio.com'))
    )
  }
)

/**
 * Selector that returns the cognito user
 * @param state - the redux state holding the authentication info
 */
export const getCognitoUserAttributes = (state: AuthenticationStateType) =>
  state[AUTHENTICATION_PATH]?.user?.attributes

/**
 * In order to show correct success or error message in the account update form, we keep track of the status of the action that was performed.
 * This is related to the action which is performed by Cognito.
 * @param state - the redux state holding the authentication info
 */
export const getLastAction = (state: AuthenticationStateType) =>
  state[AUTHENTICATION_PATH]?.lastAction

/**
 * Checks if the user profile update is pending
 * @param state - The authentication state
 * @returns True if the user profile update is pending, false otherwise
 */
export const getUserProfileUpdatePending = (state: AuthenticationStateType) =>
  getLastAction(state) === updateUserAttributes.pending.type

export const selectUser = createSelector(getUser, user => user)

/**
 * Selector that returns the Cognito user tier
 */
export const selectCognitoUserTier = createSelector(getUser, user =>
  getCognitoUserTier(user)
)

/**
 * Gets the user identifiable traits
 * @param state - The authentication state
 * @returns The user identifiable traits or null if no user
 */
export const getUserIdentifiableTraits = (
  state: AuthenticationStateType
): UserIdentifiableTraits | null => {
  const user = state?.authentication?.user
  if (!user) return null

  const legacy_id = getUserLegacyId(user)
  const tier = getCognitoUserTier(user)
  const userPoolName = getUserPoolName(state?.authentication?.userPoolId)

  return {
    legacy_id,
    tier,
    userPoolName
  }
}

/**
 * Gets the authentication method used by the user
 * @param state - The authentication state
 * @returns The authentication method
 */
export const getAuthenticationMethod = (state: AuthenticationStateType) => {
  const providerName = safeJsonParse<UserAttributeIdentities[]>(
    state?.authentication?.user?.attributes?.identities
  )?.[0]?.providerName
  if (providerName === 'SignInWithApple') return Connections.Apple

  return providerName || Connections.Google
}

// @todo - move getIsRtlLanguage to core/localization
interface MetaState {
  userCountryCode: string
  languageCode: string
  isRTLLanguage: boolean
}

export interface State {
  meta: MetaState
}

/**
 * Checks if the language is RTL
 * @param state - The application state
 * @returns True if the language is RTL, false otherwise
 */
export const getIsRtlLanguage = (state: State): boolean =>
  Boolean(state.meta.isRTLLanguage)

/**
 * Returns whether the auth is enabled for the current brand.
 * It is based on whether the brand is a meta to meta brand or not.
 */
export const selectAuthEnabled = createSelector(
  getBrandShowSignIn,
  getBrandShowSignIn => getBrandShowSignIn
)

/**
 * Selector that returns the account URL for the current brand
 */
export const selectAccountUrl = createSelector(
  getBrand,
  brand => `${brand.siteAddresses.address}/user/account`
)

/**
 * Checks if the user is an employee
 * @param user - The user object
 * @returns True if the user is an employee, false otherwise
 */
export const getIsEmployee = (user?: User | null) => {
  const userTierFromCookie = getCognitoUserTierFromCookie()
  const userTierFromMetaData = (user && getUserTier(user)) || []
  const userTier =
    userTierFromCookie.length > 0 ? userTierFromCookie : userTierFromMetaData

  return getIsUserTierEmployee(userTier)
}

export const selectIsEmployee = createSelector(selectUser, getIsEmployee)

/**
 * Checks if the user is a member plus
 * @returns True if the user is a member plus, false otherwise
 */
export const getIsMemberPlus = () =>
  getCognitoHasPlusTier(getCognitoUserTierFromCookie() as []) || false

/**
 * Selector that returns if the user is a member plus
 */
export const selectIsMemberPlus = createSelector(
  selectCognitoUserTier,
  userTier => getCognitoHasPlusTier(userTier) || getIsMemberPlus()
)

/**
 * Selector that returns if an offer is private or not, based on the offer accessTier and user state.
 * Keep the logic synced with isPrivateDeal.
 * @param offer - The offer to check
 */
export const getIsPrivateDeal =
  (offer?: GenericOffer | Partial<SearchOffer> | Partial<Offer> | null) =>
  (state: AuthenticationStateType & BrandState): boolean => {
    return !!isPrivateDeal(offer, getShouldSeeOffersUnlocked(state))
  }

/**
 * Selector that returns if a deal is private
 */
export const selectIsPrivateDeal = createSelector(
  (state: AuthenticationStateType & BrandState) => (offer: GenericOffer) =>
    getIsPrivateDeal(offer)(state),
  isPrivateDeal => isPrivateDeal
)

/**
 * Checks if a deal is locked
 * @param offer - The offer to check
 * @returns A function that takes a state and returns whether the deal is locked
 */
export const getIsLockedDeal =
  (offer?: GenericOffer | null) =>
  (state: AuthenticationStateType & BrandState): boolean =>
    !getShouldSeeOffersUnlocked(state) && getIsPrivateDeal(offer)(state)

const getSusiState = (state: AuthenticationStateType): SusiHistoryState =>
  state.authentication.susiHistoryState

/**
 * Selector that returns the SUSI active state
 * @param state - The authentication state
 * @returns The SUSI active state
 */
export const getSusiActive = createSelector(
  getSusiState,
  susiState => susiState.active
)

/**
 * Selector that returns the SUSI analytics data
 * @param state - The authentication state
 * @returns The SUSI analytics data
 */
export const getSusiAnalyticsData = createSelector(
  getSusiState,
  susiState => susiState.analyticsData
)

export {Connections, getCognitoUserTier, getConnection}
